/**
 * Custom Properties - File Upload Handler
 * Handles file uploads for product customization
 */

class CustomPropertiesUploader {
  constructor() {
    this.fileInputs = document.querySelectorAll('.custom-property-file-input');
    this.init();
    this.attachToForm();
  }

  init() {
    this.fileInputs.forEach(input => {
      input.addEventListener('change', (e) => this.handleFileSelect(e));
    });
  }

  attachToForm() {
    // Find all product forms and attach submit handler
    const productForms = document.querySelectorAll('form[action*="/cart/add"]');

    productForms.forEach(form => {
      form.addEventListener('submit', (e) => {
        // Get custom properties container
        const propertiesContainer = document.querySelector('.product-custom-properties');
        if (!propertiesContainer) return;

        // Get all property inputs
        const textInputs = propertiesContainer.querySelectorAll('input[name^="properties"]');
        const hiddenInputs = propertiesContainer.querySelectorAll('input.file-url-input');

        // Clone and append to form if not already there
        textInputs.forEach(input => {
          if (input.value && !form.contains(input)) {
            const clone = input.cloneNode(true);
            clone.style.display = 'none';
            form.appendChild(clone);
          }
        });

        hiddenInputs.forEach(input => {
          if (input.value && !form.contains(input)) {
            const clone = input.cloneNode(true);
            clone.style.display = 'none';
            form.appendChild(clone);
          }
        });
      });
    });
  }

  async handleFileSelect(event) {
    const input = event.target;
    const file = input.files[0];

    if (!file) return;

    const wrapper = input.closest('.file-upload-wrapper');
    const filenameDisplay = wrapper.querySelector('.file-upload-filename');
    const hiddenInput = wrapper.querySelector('.file-url-input');

    // Validate file
    const validation = this.validateFile(file);
    if (!validation.valid) {
      this.showError(wrapper, validation.message);
      input.value = '';
      return;
    }

    // Show filename
    filenameDisplay.textContent = file.name;

    // Process file
    try {
      wrapper.classList.add('uploading');
      const fileUrl = await this.createImageUrl(file);

      // Store the URL in hidden input
      hiddenInput.value = fileUrl;

      // Success state
      wrapper.classList.remove('uploading');
      wrapper.classList.add('success');

      console.log('File processed successfully');
    } catch (error) {
      console.error('Processing failed:', error);
      this.showError(wrapper, 'Failed to process image. Please try again.');
      wrapper.classList.remove('uploading');
      input.value = '';
      hiddenInput.value = '';
      filenameDisplay.textContent = '';
    }
  }

  validateFile(file) {
    const maxSize = 20 * 1024 * 1024; // 20MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        message: 'Please upload a valid image file (JPG, PNG, GIF, WEBP)'
      };
    }

    if (file.size > maxSize) {
      return {
        valid: false,
        message: 'File size must be less than 20MB'
      };
    }

    return { valid: true };
  }

  async createImageUrl(file) {
    // Convert image to compressed base64 data URL
    // This stores the image in cart properties without needing external storage

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();

        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Calculate new dimensions (max 1200px for better quality)
          let width = img.width;
          let height = img.height;
          const maxDimension = 1200;

          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;

          // Draw image with high quality
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to base64 with good compression
          // Using JPEG format with 85% quality for best size/quality ratio
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.85);

          // Check if compressed image is too large
          const sizeInBytes = (compressedDataUrl.length * 3) / 4;
          const sizeInMB = sizeInBytes / (1024 * 1024);

          if (sizeInMB > 2) {
            // If still too large, compress more
            const moreCompressed = canvas.toDataURL('image/jpeg', 0.7);
            resolve(moreCompressed);
          } else {
            resolve(compressedDataUrl);
          }
        };

        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };

        img.src = e.target.result;
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsDataURL(file);
    });
  }

  showError(wrapper, message) {
    // Remove existing error
    const existingError = wrapper.parentElement.querySelector('.error-message');
    if (existingError) {
      existingError.remove();
    }

    // Add error message
    const errorDiv = document.createElement('small');
    errorDiv.className = 'error-message';
    errorDiv.style.color = '#ff0000';
    errorDiv.style.fontSize = '0.8125rem';
    errorDiv.style.marginTop = '0.25rem';
    errorDiv.textContent = message;
    wrapper.parentElement.appendChild(errorDiv);

    // Add error class
    wrapper.parentElement.classList.add('error');

    // Remove error after 5 seconds
    setTimeout(() => {
      errorDiv.remove();
      wrapper.parentElement.classList.remove('error');
    }, 5000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CustomPropertiesUploader();
  });
} else {
  new CustomPropertiesUploader();
}

// Re-initialize on Shopify section events (for theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new CustomPropertiesUploader();
  });
}
